/* ==========================================================================
   LAUZON — global behaviour
   Full port of the house-style prototype main.js, minus page-specific bits
   (PDP gallery, collection filters) which ship with their own sections.

   Motion dial: MEDIUM-LOW, matching the live site. Transform/opacity/color
   only, fully disabled under prefers-reduced-motion, and hardened against
   hidden/throttled tabs (frozen rAF must never strand content invisible).

   Everything is idempotent (data-flags) and re-runs on
   shopify:section:load so the theme editor stays live-editable.
   ========================================================================== */

(function () {
  'use strict';

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- Plain JS: works with or without GSAP ---------- */

  var menuBtn = document.querySelector('.menu-btn');
  if (menuBtn) {
    menuBtn.addEventListener('click', function () {
      var open = document.body.classList.toggle('nav-open');
      menuBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* Carousel rails: arrows step one visible page, scroll-snap does the rest.

     WRAP-AROUND (Dave S., 2026-08-22): every carousel in this theme cycles.
     Next from the last page returns to the start, prev from the start jumps
     to the end. Implemented as a scroll-position wrap rather than cloned
     items — these rails are native overflow with scroll-snap, and cloning
     would double every card in the DOM (and the accessibility tree) for a
     12-card rail. `stepRail` is the one place that behaviour lives, so the
     brands rail and any future rail get it for free.

     Two things make the end tests less trivial than they look, and both were
     found by measurement rather than reasoning:

     1. scrollLeft is fractional on zoomed and hi-dpi displays, so a bare
        `scrollLeft >= max` is never exactly true and the wrap silently never
        fires. Hence a tolerance.
     2. The tolerance cannot be a flat 2px. These rails have inline padding
        and scroll-snap-align: start, so the FIRST snap point on the brands
        rail is scrollLeft 64, not 0 — the rail sits at rest 64px in, "at the
        start" reads as false, and prev-from-the-start refuses to wrap. The
        tolerance therefore includes the rail's own inline padding. */
  function railPad(rail) {
    var cs = getComputedStyle(rail);
    return {
      start: parseFloat(cs.paddingInlineStart || cs.paddingLeft) || 0,
      end: parseFloat(cs.paddingInlineEnd || cs.paddingRight) || 0
    };
  }
  function railAtEnd(rail) {
    return rail.scrollLeft >= (rail.scrollWidth - rail.clientWidth - railPad(rail).end - 2);
  }
  function railAtStart(rail) {
    return rail.scrollLeft <= railPad(rail).start + 2;
  }

  function stepRail(rail, dir, factor) {
    var behavior = reducedMotion() ? 'auto' : 'smooth';
    var amount = rail.clientWidth * (factor || 1);
    if (dir > 0 && railAtEnd(rail)) {
      rail.scrollTo({ left: 0, behavior: behavior });
    } else if (dir < 0 && railAtStart(rail)) {
      rail.scrollTo({ left: rail.scrollWidth - rail.clientWidth, behavior: behavior });
    } else {
      rail.scrollBy({ left: dir * amount, behavior: behavior });
    }
  }

  // Delegated so sections added in the theme editor work without re-init.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-rail-prev], [data-rail-next]');
    if (!btn) return;
    var id = btn.getAttribute('data-rail-prev') || btn.getAttribute('data-rail-next');
    var rail = document.getElementById(id);
    if (!rail) return;
    stepRail(rail, btn.hasAttribute('data-rail-next') ? 1 : -1);
  });

  // Podcast wave bars (deterministic heights, no Math.random)
  function buildWaves(root) {
    root.querySelectorAll('.podcast-wave').forEach(function (wave) {
      if (wave.childElementCount) return;
      var heights = [12, 26, 40, 18, 34, 22, 44, 14, 30, 38, 20, 42, 16, 28, 36, 24, 40, 12, 32, 44, 18, 26, 38, 14];
      heights.forEach(function (h) {
        var bar = document.createElement('span');
        bar.style.height = h + 'px';
        wave.appendChild(bar);
      });
    });
  }
  buildWaves(document);

  /* Ticker fill.
     The loop is a GSAP xPercent -50 wrap, which is only seamless when the
     first half of the track is at least as wide as the bar. The section ships
     ONE set of items; this clones it to an EVEN number of sets, the first
     half of which covers the bar. Without it the marquee ran out of content
     mid-screen and left a gap before the next copy caught up.

     Runs outside the motion gate on purpose: under prefers-reduced-motion
     nothing animates, but the bar should still read as a full row rather
     than five phrases and a stretch of white. */
  function fillTicker(track) {
    var host = track.parentElement;
    if (!host) return;
    var set = track.dataset.tickerSet
      ? null
      : Array.prototype.slice.call(track.children);
    if (set) {
      if (!set.length) return;
      track.dataset.tickerSet = set.length;
      track._tickerSet = set.map(function (el) { return el.cloneNode(true); });
    }
    var setCount = track.children.length / parseInt(track.dataset.tickerSet, 10);
    var setWidth = track.scrollWidth / setCount;
    if (!setWidth) return;
    // Half the track has to cover the bar, so: sets/2 * setWidth >= hostWidth.
    var wanted = Math.max(1, Math.ceil(host.clientWidth / setWidth)) * 2;
    for (var n = setCount; n < wanted; n++) {
      track._tickerSet.forEach(function (el) { track.appendChild(el.cloneNode(true)); });
    }
  }

  function buildTickers(root) {
    root.querySelectorAll('.ticker-track').forEach(fillTicker);
  }
  buildTickers(document);

  // Widening the window can leave the bar short again. Topping up keeps the
  // set count even, so the -50% wrap stays on a set boundary and a running
  // tween needs no restart.
  var tickerResize = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(tickerResize);
    tickerResize = window.setTimeout(function () { buildTickers(document); }, 200);
  });

  // Brands rail: chevrons step a page; dashes track scroll position
  /* Brands rail: chevrons page it, and the dash row underneath is BUILT HERE.

     It used to be one dash per logo, rendered in Liquid. That was wrong twice
     over: 22 logos is 3 pages, so 22 dashes told you nothing about where you
     were, and they were inert `<span>`s inside an aria-hidden row. Dave's
     note, 2026-08-22: "if there are three levels of scroll you can do, then
     there should just be three lines at the bottom."

     Page count depends on the viewport, so only the browser can count it —
     hence JS, and hence a rebuild on resize. The dashes are real buttons that
     page the rail. */
  function initBrands(root) {
    root.querySelectorAll('.brands-rail').forEach(function (brandsRail) {
      if (brandsRail.dataset.brandsInit) return;
      brandsRail.dataset.brandsInit = '1';
      var section = brandsRail.closest('.brands') || document;
      var bPrev = section.querySelector('.brands-arrow--prev');
      var bNext = section.querySelector('.brands-arrow--next');
      [bPrev, bNext].forEach(function (b) {
        if (!b) return;
        b.removeAttribute('aria-hidden');
        b.removeAttribute('tabindex');
        b.setAttribute('aria-label', b === bPrev ? 'Previous brands' : 'Next brands');
      });
      if (bPrev) bPrev.addEventListener('click', function () { stepRail(brandsRail, -1, 1); });
      if (bNext) bNext.addEventListener('click', function () { stepRail(brandsRail, 1, 1); });

      var dashRow = section.querySelector('[data-brands-dashes]');
      if (!dashRow) return;

      function pageCount() {
        var page = brandsRail.clientWidth;
        if (!page) return 1;
        // Round rather than ceil: a rail overhanging by a few pixels is one
        // page, not two, and a phantom final dash that scrolls nowhere is
        // exactly the kind of dead control this replaced.
        return Math.max(1, Math.round(brandsRail.scrollWidth / page));
      }

      function currentPage(pages) {
        var max = brandsRail.scrollWidth - brandsRail.clientWidth;
        if (max <= 0) return 0;
        return Math.min(pages - 1, Math.round((brandsRail.scrollLeft / max) * (pages - 1)));
      }

      function paint() {
        var kids = dashRow.children;
        var at = currentPage(kids.length);
        for (var i = 0; i < kids.length; i++) {
          var on = i === at;
          kids[i].classList.toggle('is-active', on);
          if (on) { kids[i].setAttribute('aria-current', 'true'); }
          else { kids[i].removeAttribute('aria-current'); }
        }
      }

      function build() {
        var pages = pageCount();
        // One page means nothing to page through: render no row at all
        // rather than a single dash that does nothing.
        if (pages < 2) { dashRow.textContent = ''; return; }
        if (dashRow.children.length !== pages) {
          dashRow.textContent = '';
          for (var i = 0; i < pages; i++) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'brands-dash';
            b.setAttribute('aria-label', 'Brands, page ' + (i + 1) + ' of ' + pages);
            (function (idx) {
              b.addEventListener('click', function () {
                var max = brandsRail.scrollWidth - brandsRail.clientWidth;
                brandsRail.scrollTo({
                  left: max * (idx / (pages - 1)),
                  behavior: reducedMotion() ? 'auto' : 'smooth'
                });
              });
            })(i);
            dashRow.appendChild(b);
          }
        }
        paint();
      }

      build();
      brandsRail.addEventListener('scroll', paint, { passive: true });

      var t = null;
      window.addEventListener('resize', function () {
        window.clearTimeout(t);
        t = window.setTimeout(build, 200);
      });
    });
  }

  initBrands(document);

  /* ---------- GSAP motion (skipped entirely without the assets) ---------- */

  function motionInit() {
    if (!window.gsap || !window.ScrollTrigger) return;

    gsap.registerPlugin(ScrollTrigger);

    // Sticky header compacts on scroll (class toggle; CSS transitions it)
    ScrollTrigger.create({
      start: 60,
      end: 'max',
      toggleClass: { targets: '.site-header', className: 'is-compact' }
    });

    var mm = gsap.matchMedia();

    mm.add(
      {
        motionOK: '(prefers-reduced-motion: no-preference)',
        reduce: '(prefers-reduced-motion: reduce)'
      },
      function (context) {
        if (context.conditions.reduce) {
          // Static site. Everything stays visible; no tweens are created.
          return;
        }

        // Hero entrance: quick, quiet stagger on load
        var heroEls = gsap.utils.toArray('[data-hero]');
        if (heroEls.length) {
          gsap.set(heroEls, { autoAlpha: 0, y: 24 });
          gsap.to(heroEls, {
            autoAlpha: 1,
            y: 0,
            duration: 0.7,
            ease: 'power3.out',
            stagger: 0.09,
            delay: 0.05,
            clearProps: 'transform'
          });
        }

        revealAll(document);

        // Continuous loops: scene ticker (content duplicated in markup;
        // -50% wrap for the seamless loop). Pause on hover.
        document.querySelectorAll('.ticker-track').forEach(function (track) {
          // Speed is per-set, not per-track: cloning more sets to fill a wide
          // bar must not make the type crawl. 26s was tuned on a two-set
          // track, so that is the reference.
          fillTicker(track);
          var sets = track.children.length / (parseInt(track.dataset.tickerSet, 10) || track.children.length);
          var duration = 26 * (sets / 2);
          var tween = gsap.to(track, { xPercent: -50, duration: duration, ease: 'none', repeat: -1 });
          var host = track.parentElement;
          host.addEventListener('mouseenter', function () { tween.pause(); });
          host.addEventListener('mouseleave', function () { tween.play(); });
        });

        // Podcast wave: bars breathe like live audio
        gsap.utils.toArray('.podcast-wave span').forEach(function (bar, i) {
          gsap.to(bar, {
            scaleY: 0.35 + ((i * 7) % 10) / 18,
            duration: 0.5 + ((i * 3) % 7) / 10,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
            delay: (i % 5) * 0.09
          });
        });

        // Sell/Build panels: cut-outs drift gently
        gsap.utils.toArray('[data-float]').forEach(function (el, i) {
          gsap.to(el, { y: -12, duration: 2.6 + i * 0.4, ease: 'sine.inOut', yoyo: true, repeat: -1 });
        });

        // Heritage marquee: opposing continuous drift (one row LTR / one RTL).
        // Track content x3 in markup + cloned once here for the -50% wrap.
        document.querySelectorAll('.hm-track[data-marquee]').forEach(function (track) {
          if (!track.dataset.cloned) {
            track.dataset.cloned = '1';
            Array.prototype.slice.call(track.children).forEach(function (el) {
              track.appendChild(el.cloneNode(true));
            });
          }
          var dir = parseFloat(track.getAttribute('data-marquee')) || -1;
          if (dir > 0) {
            gsap.set(track, { xPercent: -50 });
            gsap.to(track, { xPercent: 0, duration: 48, ease: 'none', repeat: -1 });
          } else {
            gsap.to(track, { xPercent: -50, duration: 48, ease: 'none', repeat: -1 });
          }
        });

        // Heritage statement: word-by-word colour reveal on scroll — the
        // live site's signature move. Scrubbed from top 80% -> top 30%.
        document.querySelectorAll('[data-reveal-type]').forEach(function (el) {
          if (!el.dataset.split) {
            el.dataset.split = '1';
            Array.prototype.slice.call(el.childNodes).forEach(function (node) {
              if (node.nodeType !== 3 || !node.textContent.trim()) return;
              var frag = document.createDocumentFragment();
              node.textContent.split(/(\s+)/).forEach(function (part) {
                if (!part) return;
                if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
                var w = document.createElement('span');
                w.className = 'rv-word';
                w.textContent = part;
                frag.appendChild(w);
              });
              el.replaceChild(frag, node);
            });
          }
          gsap.fromTo(el.querySelectorAll('.rv-word'),
            { color: el.dataset.bgColor || '#D5DAE1' },
            {
              color: el.dataset.fgColor || '#272F3A',
              duration: 0.3,
              stagger: 0.02,
              scrollTrigger: {
                trigger: el,
                start: 'top 80%',
                end: 'top 30%',
                scrub: true
              }
            }
          );
        });

        ScrollTrigger.refresh();

        return function () {
          gsap.set('[data-hero], [data-reveal]', { clearProps: 'all' });
        };
      }
    );

    initCardHover(document);
  }

  // Scroll reveals: fade-up, 70ms stagger, once. Scoped so the theme editor
  // can re-run it over a single newly-inserted section.
  function revealAll(root) {
    if (!window.gsap || !window.ScrollTrigger || reducedMotion()) return;
    var reveals = gsap.utils.toArray(root.querySelectorAll('[data-reveal]')).filter(function (el) {
      return !el.dataset.revealed;
    });
    if (!reveals.length) return;
    reveals.forEach(function (el) { el.dataset.revealed = 'pending'; });
    gsap.set(reveals, { autoAlpha: 0, y: 26 });
    ScrollTrigger.batch(reveals, {
      start: 'top 88%',
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          stagger: 0.07,
          overwrite: true,
          clearProps: 'transform'
        });
      }
    });
  }

  // Card hover: lift -6px, image scale 1.03 (pointer: fine only)
  function initCardHover(root) {
    if (!window.gsap) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches || reducedMotion()) return;
    root.querySelectorAll('.card, .cat-card').forEach(function (card) {
      if (card.dataset.hoverInit) return;
      card.dataset.hoverInit = '1';
      var lift = card.querySelector('.card-lift') || card;
      /* querySelectorAll, not querySelector: a product card can now hold two
         stacked photos (the hover crossfade in lauzon-product-card.css). If
         only the first were scaled, the second would fade in at rest size
         mid-tween and the swap would read as a jump. GSAP takes the NodeList
         as-is, and a card with one image behaves exactly as before. */
      var imgs = card.querySelectorAll('.card-media img, .cat-media img');
      card.addEventListener('mouseenter', function () {
        gsap.to(lift, { y: -6, duration: 0.25, ease: 'power2.out' });
        if (imgs.length) gsap.to(imgs, { scale: 1.03, duration: 0.3, ease: 'power2.out' });
      });
      card.addEventListener('mouseleave', function () {
        gsap.to(lift, { y: 0, duration: 0.3, ease: 'power2.out' });
        if (imgs.length) gsap.to(imgs, { scale: 1, duration: 0.35, ease: 'power2.out' });
      });
    });
  }

  if (document.hidden && window.gsap) {
    /* Loaded in a background tab: rAF is frozen and entrance tweens would
       strand content invisible. Stay fully static; reload clean when shown. */
    document.addEventListener('visibilitychange', function once() {
      if (!document.hidden) {
        document.removeEventListener('visibilitychange', once);
        window.location.reload();
      }
    });
  } else {
    motionInit();
  }

  /* ---------- Theme editor ---------- */

  document.addEventListener('shopify:section:load', function (event) {
    buildWaves(event.target);
    buildTickers(event.target);
    initBrands(event.target);
    revealAll(event.target);
    initCardHover(event.target);
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  // Exposed so section scripts can reveal content they inject.
  window.LauzonMotion = { reveal: revealAll, cardHover: initCardHover };

  /* Failsafe: never let entrance/reveal states strand content invisible
     (throttled rAF in hidden/background tabs freezes tweens mid-run). */
  function rescue() {
    if (!window.gsap) return;
    document.querySelectorAll('[data-hero],[data-reveal]').forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.99) {
        gsap.set(el, { clearProps: 'all' });
      }
    });
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  }
  window.setTimeout(rescue, 3000);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') window.setTimeout(rescue, 1200);
  });
})();

/* ---------------------------------------------------------------------------
   Chat CTAs -> Chatra widget.
   Any [data-lz-chat] button opens the chat. If the button carries
   data-ask-product, the instrument is written to the visitor's notes so
   whoever picks it up already knows which guitar (Chatra shows the agent
   the page too). Buttons sit beside a real tel: link, which is the fallback
   when the widget never loads. window.Chatra is a queue stub from the app
   embed's loader, so calls made before the widget finishes loading are
   replayed once it has - no ready event needed.
   --------------------------------------------------------------------------- */
(function () {
  function chatra() {
    return typeof window.Chatra === 'function' ? window.Chatra : null;
  }

  function openChat(btn) {
    var api = chatra();
    if (!api) return false;
    var name = btn.getAttribute('data-ask-product');
    if (name) {
      var sku = btn.getAttribute('data-ask-sku') || '';
      api('setIntegrationData', {
        notes: 'Asking about: ' + name + (sku ? ' (SKU ' + sku + ')' : '')
      });
    }
    api('openChat', true);
    return true;
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-lz-chat]');
    if (!btn) return;
    event.preventDefault();
    openChat(btn);
  });

  /* Chatra's root (#chatra) is position: fixed at z-index 2147483647 and
     ignores ChatraSetup.zIndex in widget mode, so no overlay can out-stack it,
     and on phones the launcher sits exactly where the product lightbox's next
     arrow lands. lauzon-product.js flags an open lightbox with
     .lz-lightbox-open on <html>; hide the root while it is there and put it
     back on close. Done on the element rather than through Chatra('hide') so
     it is immediate and does not depend on the widget's own state. */
  var html = document.documentElement;
  var hidden = false;
  new MutationObserver(function () {
    var open = html.classList.contains('lz-lightbox-open');
    if (open === hidden) return;
    var root = document.getElementById('chatra');
    if (!root) return;
    hidden = open;
    root.style.display = open ? 'none' : '';
  }).observe(html, { attributes: true, attributeFilter: ['class'] });
})();
